import type { Move, Performance } from "../data/schema";

export interface Frame {
  pos: Record<string, [number, number]>;
  facing: Record<string, number>;
}

const DEFAULT_MS = 1400;
const DEFAULT_FACING = 90; // facing the judges

function ease(name: string | undefined, t: number): number {
  if (name === "linear") return t;
  // default inOutQuad
  return t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2;
}

// shortest-arc interpolation between two headings (degrees)
function lerpAngle(a: number, b: number, t: number): number {
  let d = ((b - a + 540) % 360) - 180;
  return a + d * t;
}

// smooth control points into a Catmull-Rom curve passing through every point
function catmullRom(pts: [number, number][], segs = 16): [number, number][] {
  if (pts.length < 3) return pts;
  const P = [pts[0], ...pts, pts[pts.length - 1]];
  const out: [number, number][] = [];
  for (let i = 1; i < P.length - 2; i++) {
    const [p0, p1, p2, p3] = [P[i - 1], P[i], P[i + 1], P[i + 2]];
    for (let j = 0; j < segs; j++) {
      const t = j / segs, t2 = t * t, t3 = t2 * t;
      const x = 0.5 * (2 * p1[0] + (-p0[0] + p2[0]) * t + (2 * p0[0] - 5 * p1[0] + 4 * p2[0] - p3[0]) * t2 + (-p0[0] + 3 * p1[0] - 3 * p2[0] + p3[0]) * t3);
      const y = 0.5 * (2 * p1[1] + (-p0[1] + p2[1]) * t + (2 * p0[1] - 5 * p1[1] + 4 * p2[1] - p3[1]) * t2 + (-p0[1] + 3 * p1[1] - 3 * p2[1] + p3[1]) * t3);
      out.push([x, y]);
    }
  }
  out.push(pts[pts.length - 1]);
  return out;
}

// sample a polyline at fraction e (0..1); returns [x, y, tangentDeg]
function samplePoly(pts: [number, number][], e: number): [number, number, number] {
  const cum = [0];
  for (let i = 1; i < pts.length; i++)
    cum.push(cum[i - 1] + Math.hypot(pts[i][0] - pts[i - 1][0], pts[i][1] - pts[i - 1][1]));
  const total = cum[cum.length - 1];
  if (total < 1e-6) return [pts[0][0], pts[0][1], NaN];
  const d = e * total;
  let k = 1;
  while (k < cum.length - 1 && cum[k] < d) k++;
  const seg = cum[k] - cum[k - 1] || 1;
  const f = (d - cum[k - 1]) / seg;
  const x = pts[k - 1][0] + (pts[k][0] - pts[k - 1][0]) * f;
  const y = pts[k - 1][1] + (pts[k][1] - pts[k - 1][1]) * f;
  const tangent = (Math.atan2(pts[k][1] - pts[k - 1][1], pts[k][0] - pts[k - 1][0]) * 180) / Math.PI;
  return [x, y, tangent];
}

export class ChoreoEngine {
  private moves: Move[];
  private groups: Record<string, string[]> = {};

  constructor(public perf: Performance) {
    this.moves = [...perf.moves].sort((a, b) => a.startSec - b.startSec);
    for (const g of perf.groups) this.groups[g.id] = g.skaters;
  }

  private who(m: Move): string[] {
    return m.group ? this.groups[m.group] ?? [] : m.skaters ?? [];
  }

  sampleAt(t: number): Frame {
    const pos: Record<string, [number, number]> = {};
    const facing: Record<string, number> = {};
    for (const id in this.perf.startPositions) {
      const p = this.perf.startPositions[id];
      pos[id] = [p[0], p[1]];
      facing[id] = p.length > 2 ? (p[2] as number) : DEFAULT_FACING;
    }
    for (const m of this.moves) {
      if (t < m.startSec) continue;
      this.apply(m, pos, facing, t);
    }
    return { pos, facing };
  }

  private apply(m: Move, pos: Frame["pos"], facing: Frame["facing"], t: number) {
    const dur = (m.durationMs ?? DEFAULT_MS) / 1000;
    const s1 = m.startSec + dur;
    const fracLin = t >= s1 ? 1 : dur > 0 ? (t - m.startSec) / dur : 1;
    const ids = this.who(m);

    if (m.type === "rotate") {
      const cx = m.cx ?? 0,
        cy = m.cy ?? 0;
      let ang: number;
      if (m.deg != null) ang = m.deg * ease(m.ease, fracLin);
      else if (m.rateDegPerSec != null)
        ang = m.rateDegPerSec * (Math.min(t, s1) - m.startSec); // linear in real seconds
      else ang = 0;
      const r = (ang * Math.PI) / 180,
        ca = Math.cos(r),
        sa = Math.sin(r);
      for (const id of ids) {
        const p = pos[id];
        if (!p) continue;
        const dx = p[0] - cx,
          dy = p[1] - cy;
        pos[id] = [cx + dx * ca - dy * sa, cy + dx * sa + dy * ca];
        const f = m.facing;
        if (f === undefined || f === "spin") facing[id] = facing[id] + ang;
        else if (typeof f !== "string") this.explicitFacing(f as number | Record<string, number>, id, facing, ease(m.ease, fracLin));
      }
      return;
    }

    // path / hold
    const e = ease(m.ease, fracLin);
    for (const id of ids) {
      const start = pos[id];
      if (!start) continue;
      const target = m.to && m.to[id] ? m.to[id] : start;
      const wp = m.waypoints && m.waypoints[id];
      let pts: [number, number][] = [start, ...(wp ?? []), target];
      if (m.arcM && !wp) {
        const mx = (start[0] + target[0]) / 2,
          my = (start[1] + target[1]) / 2;
        const vx = target[0] - start[0],
          vy = target[1] - start[1];
        const len = Math.hypot(vx, vy);
        if (len > 1e-3) pts = [start, [mx - (vy / len) * m.arcM, my + (vx / len) * m.arcM], target];
      }
      const curve = pts.length > 2 ? catmullRom(pts) : pts;
      const [nx, ny, tangent] = samplePoly(curve, m.type === "hold" ? 0 : e);
      pos[id] = [nx, ny];
      const moved = Math.hypot(nx - start[0], ny - start[1]) > 1e-3 || !Number.isNaN(tangent);
      const f = m.facing;
      if (f === "hold") {
        /* keep */
      } else if (f === undefined || f === "tangent") {
        if (moved && !Number.isNaN(tangent)) facing[id] = tangent;
      } else if (f === "reverse") {
        if (moved && !Number.isNaN(tangent)) facing[id] = tangent + 180; // travelling backwards
      } else if (f === "flip") {
        facing[id] = facing[id] + 180 * e; // turn around, relative to current facing
      } else if (f && typeof f === "object" && "around" in f) {
        const [ax, ay] = (f as { around: [number, number] }).around;
        facing[id] = (Math.atan2(pos[id][1] - ay, pos[id][0] - ax) * 180) / Math.PI + 90; // CCW tangent
      } else if (typeof f !== "string") {
        this.explicitFacing(f as number | Record<string, number>, id, facing, e);
      }
    }
  }

  private explicitFacing(
    f: number | Record<string, number>,
    id: string,
    facing: Frame["facing"],
    e: number
  ) {
    const target = typeof f === "number" ? f : f[id];
    if (target == null) return;
    facing[id] = lerpAngle(facing[id], target, e);
  }
}
